import { useMemo, useCallback } from 'react'
import { ZIP_REGEX, FeeValidationErrorTypes, LOCATION_TYPES } from '../constants/Validation'
import {
  FEE_MGMT_COLUMNS,
  FEE_MGMT_COLS_COLUMN_KEYS,
  OVERRIDE_COLUMNS_KEYS,
  OVERRIDE_COLUMNS_READABLE,
} from '../constants/Columns'
import {
  OVERRIDE_TYPES,
  REQUIRED_COLUMNS_BY_OVERRIDE_TYPE_READABLE,
  REQUIRED_COLUMNS_BY_OVERRIDE_TYPE,
} from '../constants/OverrideTypes'
import { normalizeStatesInput, normalizeCountiesForState } from '../utils/stateHelpers'

const KEY_SEPARATOR = '|||'

// Helper functions for multi-value fields
const parseMultiValue = (value) => {
  if (value && typeof value === 'string') {
    // If value is a non-empty string, split and process
    return new Set(
      value
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .sort(),
    )
  } else if (Array.isArray(value)) {
    // If value is already an array, process its elements
    return new Set(
      value
        .map((v) => (typeof v === 'string' ? v.trim() : v)) // Trim if string
        .filter(Boolean) // Filter out empty/falsy values
        .sort(), // Sort the resulting elements
    )
  } else {
    // For any other type or empty/null/undefined value, return an empty Set
    return new Set()
  }
}

const stringifyMultiValue = (valueSet) => {
  if (!valueSet || valueSet.size === 0) {
    return ''
  }
  return Array.from(valueSet).join(', ')
}

// Helper to create a new column validation state
const createColumnValidation = () => ({
  errorTypes: new Set(),
  details: {},
})

// Validation helper functions
const createValidationSummary = () => {
  const errorCounts = {}
  const errorRows = {}

  Object.keys(FeeValidationErrorTypes).forEach((key) => {
    const type = FeeValidationErrorTypes[key]
    errorCounts[type] = 0
    errorRows[type] = new Set()
  })

  return {
    errorCounts: errorCounts,
    errorRows: errorRows,
    totalErrorCount: 0,
    hasErrors: false,
  }
}

// Helper to add an error to a row's validation state
const addError = (validationState, columnName, errorType, details = null) => {
  // Initialize validationState if it doesn't exist
  if (!validationState) {
    validationState = {
      isDuplicateRow: false,
      userMarkedForDelete: false,
      validationErrors: {},
    }
  }
  // Initialize validationErrors if it doesn't exist
  if (!validationState.validationErrors) {
    validationState.validationErrors = {}
  }

  if (!validationState.validationErrors[columnName]) {
    validationState.validationErrors[columnName] = createColumnValidation()
  }
  validationState.validationErrors[columnName].errorTypes.add(errorType)
  if (details) {
    if (!validationState.validationErrors[columnName].details[errorType]) {
      validationState.validationErrors[columnName].details[errorType] = { values: [] }
    }
    validationState.validationErrors[columnName].details[errorType].values.push(details)
  }
}

const createRowKey = (row, availableColumnsMap) => {
  // Create a unique key string for each row based on the available columns
  const { BRANCH, VENDOR, LOCATION_TYPE } = availableColumnsMap
  const parts = [row[FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME] || '']

  if (BRANCH) {
    parts.push(row[FEE_MGMT_COLS_COLUMN_KEYS.BRANCH] || '')
  }
  if (VENDOR) {
    parts.push(row[FEE_MGMT_COLS_COLUMN_KEYS.VENDOR] || '')
  }
  if (LOCATION_TYPE) {
    parts.push(
      row[FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE],
      stringifyMultiValue(row.states),
      stringifyMultiValue(row.counties),
      stringifyMultiValue(row.zipCodes),
    )
  }

  return parts.join(KEY_SEPARATOR)
}

const createCacheKey = (row, rowKey) => `${row['Product Name'] || ''}${KEY_SEPARATOR}${rowKey}`

const validateMissingValues = (row, validationState, summary, availableColumnsMap) => {
  const { BRANCH, VENDOR, LOCATION_TYPE, FEE, DUE_DATE, RUSH_DUE_DATE } = availableColumnsMap

  const addMissingValueError = (columnName) => {
    addError(validationState, columnName, FeeValidationErrorTypes.MISSING_VALUE, {})
    summary.errorRows[FeeValidationErrorTypes.MISSING_VALUE].add(row.rowNumber)
  }

  if (!row[FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME]?.trim()) {
    addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME)
  }
  if (BRANCH && !row[FEE_MGMT_COLS_COLUMN_KEYS.BRANCH]?.trim()) {
    addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.BRANCH)
  }
  if (VENDOR && !row[FEE_MGMT_COLS_COLUMN_KEYS.VENDOR]?.trim()) {
    addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.VENDOR)
  }

  if (LOCATION_TYPE) {
    const rowLocationType = row[FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE]?.trim()
    switch (rowLocationType) {
      case '':
        addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE)
        break
      case LOCATION_TYPES.STATE:
        if (row.states.size === 0) {
          addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.STATES)
        }
        if (row.counties.size > 0) {
          addError(
            validationState,
            FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES,
            FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE,
            { value: 'County not allowed for Multi-State Location Type' },
          )
          summary.errorRows[FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE].add(row.rowNumber)
        }
        if (row.zipCodes.size > 0) {
          addError(
            validationState,
            FEE_MGMT_COLS_COLUMN_KEYS.ZIP_CODES,
            FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE,
            { value: 'Zip Code not allowed for Multi-State Location Type' },
          )
          summary.errorRows[FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE].add(row.rowNumber)
        }
        break
      case LOCATION_TYPES.COUNTY:
        if (row.states.size === 0) {
          addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.STATES)
        }
        if (row.zipCodes.size > 0) {
          addError(
            validationState,
            FEE_MGMT_COLS_COLUMN_KEYS.ZIP_CODES,
            FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE,
            { value: 'Zip Code not allowed for County Location Type' },
          )
          summary.errorRows[FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE].add(row.rowNumber)
        }
        if (row.counties.size === 0) {
          addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES)
        }
        break
      case LOCATION_TYPES.ZIP:
        if (row.states.size === 0) {
          addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.STATES)
        }
        if (row.counties.size > 0) {
          addError(
            validationState,
            FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES,
            FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE,
            { value: 'County not allowed for Zip Code Location Type' },
          )
          summary.errorRows[FeeValidationErrorTypes.INVALID_FIELD_FOR_LOCATION_TYPE].add(row.rowNumber)
        }
        if (row.zipCodes.size === 0) {
          addMissingValueError(FEE_MGMT_COLS_COLUMN_KEYS.ZIP_CODES)
        }
        break
      default:
        break
    }
  }

  // Get array of present fee/due date columns
  const presentOverrideColumns = [
    FEE && FEE_MGMT_COLS_COLUMN_KEYS.FEE,
    DUE_DATE && FEE_MGMT_COLS_COLUMN_KEYS.DUE_DATE,
    RUSH_DUE_DATE && FEE_MGMT_COLS_COLUMN_KEYS.RUSH_DUE_DATE,
  ].filter(Boolean)

  if (presentOverrideColumns.length > 0) {
    const hasAtLeastOneValue = presentOverrideColumns.some((col) => row[col]?.toString().trim())
    if (!hasAtLeastOneValue) {
      presentOverrideColumns.forEach((col) => addMissingValueError(col))
    }
  }
}

// Remove state/county/zip validation from validateFormats and create a specialized function
const validateLocations = (row, validationState, summary, columns) => {
  // Skip if location type column isn't available
  if (!columns.has(FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE)) {
    return
  }

  // Validate Location Type first
  const locationType = row[FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE]?.trim()
  if (locationType && !Object.values(LOCATION_TYPES).includes(locationType)) {
    addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE, FeeValidationErrorTypes.INVALID_VALUE, {
      value: locationType,
    })
    summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
  }

  // Validate ZIP codes - only applicable for ZIP location type
  if (row.zipCodes.size > 0) {
    const invalidZips = Array.from(row.zipCodes).filter((zip) => !ZIP_REGEX.test(zip))
    if (invalidZips.length > 0) {
      addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.ZIP_CODES, FeeValidationErrorTypes.INVALID_VALUE, {
        value: invalidZips.join(', '),
      })
      summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
    }
  }
}

const isUndefinedOrNull = (value) => {
  if (value === undefined || value === null) {
    return true
  }
  return false
}

const validateNumberColumn = (row, validationState, summary, columnKey, positiveOnly = false) => {
  const value = row[columnKey]
  if (isUndefinedOrNull(value)) {
    return
  }
  const numberValue = Number(value)
  if (!Number.isInteger(numberValue)) {
    addError(validationState, columnKey, FeeValidationErrorTypes.INVALID_VALUE, {
      value: row[columnKey],
    })
    summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
    return
  }
  if (positiveOnly && numberValue <= 0) {
    addError(validationState, columnKey, FeeValidationErrorTypes.INVALID_VALUE, {
      value: row[columnKey],
    })
    summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
  }
}

const validateDecimalColumn = (row, validationState, summary, columnKey) => {
  const value = row[columnKey]
  if (isUndefinedOrNull(value)) {
    return
  }
  const decimalValue = Number(value)
  if (isNaN(decimalValue)) {
    addError(validationState, columnKey, FeeValidationErrorTypes.INVALID_VALUE, {
      value: row[columnKey],
    })
    summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
  }
}

// Simplify validateFormats to only handle numeric field validation
const validateFormats = (row, validationState, summary, columns) => {
  // Validate Fee is an integer
  if (columns.has(FEE_MGMT_COLS_COLUMN_KEYS.FEE)) {
    validateDecimalColumn(row, validationState, summary, FEE_MGMT_COLS_COLUMN_KEYS.FEE)
  }

  if (columns.has(FEE_MGMT_COLS_COLUMN_KEYS.DUE_DATE)) {
    validateNumberColumn(row, validationState, summary, FEE_MGMT_COLS_COLUMN_KEYS.DUE_DATE)
  }

  if (columns.has(FEE_MGMT_COLS_COLUMN_KEYS.RUSH_DUE_DATE)) {
    validateNumberColumn(row, validationState, summary, FEE_MGMT_COLS_COLUMN_KEYS.RUSH_DUE_DATE)
  }
}

const markDuplicateRows = (productGroups, markedDuplicateRows, duplicateRowsMap) => {
  productGroups.forEach((group) => {
    if (group.length > 1) {
      group.sort((a, b) => a.rowNumber - b.rowNumber)
      const seenKeys = new Map()

      group.forEach(({ rowNumber, rowKey, row }) => {
        if (seenKeys.has(rowKey)) {
          markedDuplicateRows.add(rowNumber)
          duplicateRowsMap.set(rowNumber, seenKeys.get(rowKey))
        } else {
          if (row.userMarkedForDelete) {
            return
          }
          seenKeys.set(rowKey, rowNumber)
          row.isDuplicateRow = false
        }
      })
    }
  })
}

const trackLocation = (
  locationTypeCondition,
  locationValues,
  regex,
  keyToRows,
  keyGenerator,
  groupKey,
  row,
  validationState,
  locationType,
) => {
  if (locationTypeCondition) {
    locationValues.forEach((value) => {
      if ((regex && regex.test(value)) || !regex) {
        const key = keyGenerator(groupKey, value)
        if (!keyToRows.has(key)) {
          keyToRows.set(key, new Set())
        }
        keyToRows.get(key).add({
          rowNumber: row.rowNumber,
          validationState,
          locationType,
        })
      }
    })
  }
}

const trackLocations = (
  { validationState, row, states, counties, zipCodes, vendor, productName, branch, availableColumnsMap },
  { stateKeyToRows, countyKeyToRows, zipKeyToRows },
) => {
  if (validationState.isDuplicateRow || validationState.userMarkedForDelete) {
    return
  }
  const { BRANCH, VENDOR } = availableColumnsMap
  const parts = [productName || '']

  if (BRANCH) {
    parts.push(branch || '')
  }
  if (VENDOR) {
    parts.push(vendor || '')
  }
  const locationType = row[FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE]?.toUpperCase()

  const groupKey = parts.join(KEY_SEPARATOR)

  // Track states only for MULTI-STATE location type
  trackLocation(
    states.size > 0 && locationType === LOCATION_TYPES.STATE,
    states,
    null,
    stateKeyToRows,
    (groupKey, state) => `${groupKey}${KEY_SEPARATOR}${state}`,
    groupKey,
    row,
    validationState,
    locationType,
  )

  // Track counties only for COUNTY location type
  trackLocation(
    counties.size > 0 && states.size > 0 && locationType === LOCATION_TYPES.COUNTY,
    counties,
    null,
    countyKeyToRows,
    (groupKey, county) => {
      const state = Array.from(states)[0] // Assuming only one state for county
      return `${groupKey}${KEY_SEPARATOR}${state}${KEY_SEPARATOR}${county}`
    },
    groupKey,
    row,
    validationState,
    locationType,
  )

  // Track ZIP codes only for ZIP location type
  trackLocation(
    zipCodes.size > 0 && states.size > 0 && locationType === LOCATION_TYPES.ZIP,
    zipCodes,
    ZIP_REGEX,
    zipKeyToRows,
    (groupKey, zip) => {
      const state = Array.from(states)[0]
      return `${groupKey}${KEY_SEPARATOR}${state}${KEY_SEPARATOR}${zip}`
    },
    groupKey,
    row,
    validationState,
    locationType,
  )
}

const trackVendors = (validationState, vendor, productName, vendorKeyToRows, availableColumnsMap) => {
  if (availableColumnsMap.VENDOR && !availableColumnsMap.LOCATION_TYPE && !validationState.isDuplicateRow) {
    const vendorKey = `${productName}${KEY_SEPARATOR}${vendor}`
    if (!vendorKeyToRows.has(vendorKey)) {
      vendorKeyToRows.set(vendorKey, new Set())
    }
  }
}

const trackBranches = (validationState, branch, productName, branchKeyToRows, availableColumnsMap) => {
  if (availableColumnsMap.BRANCH && !validationState.isDuplicateRow) {
    const branchKey = `${productName}${KEY_SEPARATOR}${branch}`
    if (!branchKeyToRows.has(branchKey)) {
      branchKeyToRows.set(branchKey, new Set())
    }
  }
}

const checkDuplicateLocation = (keyToRows, locationType, readableColumn, errorType, summary) => {
  keyToRows.forEach((rowSet, key) => {
    const rowLocations = Array.from(rowSet).filter((row) => row.locationType === locationType)
    if (rowLocations.length > 1) {
      const keyParts = key.split(KEY_SEPARATOR)
      const lastKeyPart = keyParts[keyParts.length - 1]

      const rowNumbers = rowLocations.map((rl) => rl.rowNumber).sort((a, b) => a - b)
      rowLocations.forEach(({ rowNumber, validationState }) => {
        addError(validationState, readableColumn, errorType, {
          value: lastKeyPart,
          rowNumbers: rowNumbers.filter((rn) => rn !== rowNumber), // All other row numbers
        })
        summary.errorRows[errorType].add(rowNumber)
      })
    }
  })
}

const checkDuplicateLocations = (stateKeyToRows, countyKeyToRows, zipKeyToRows, summary) => {
  checkDuplicateLocation(
    stateKeyToRows,
    LOCATION_TYPES.STATE,
    FEE_MGMT_COLS_COLUMN_KEYS.STATES,
    FeeValidationErrorTypes.DUPLICATE_STATE,
    summary,
  )

  checkDuplicateLocation(
    countyKeyToRows,
    LOCATION_TYPES.COUNTY,
    FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES,
    FeeValidationErrorTypes.DUPLICATE_COUNTY,
    summary,
  )

  checkDuplicateLocation(
    zipKeyToRows,
    LOCATION_TYPES.ZIP,
    FEE_MGMT_COLS_COLUMN_KEYS.ZIP_CODES,
    FeeValidationErrorTypes.DUPLICATE_ZIP,
    summary,
  )
}

const createProductGroups = (rows, availableColumnsMap, productGroups, rowLocationMap, mappingData) => {
  rows.forEach((row) => {
    const productName = row[FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME] || ''
    const states = availableColumnsMap.LOCATION_TYPE
      ? parseMultiValue(row[FEE_MGMT_COLS_COLUMN_KEYS.STATES])
      : new Set()
    const counties = availableColumnsMap.LOCATION_TYPE
      ? parseMultiValue(row[FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES])
      : new Set()
    const zipCodes = availableColumnsMap.LOCATION_TYPE
      ? parseMultiValue(row[FEE_MGMT_COLS_COLUMN_KEYS.ZIP_CODES])
      : new Set()

    // Normalize state and county names early in the process if we have mapping data
    if (mappingData?.state_and_counties_map) {
      // Normalize states first
      if (states.size > 0) {
        const statesArray = Array.from(states)
        const { normalizedStates } = normalizeStatesInput(statesArray, mappingData.state_and_counties_map)

        if (normalizedStates.length > 0) {
          row[FEE_MGMT_COLS_COLUMN_KEYS.STATES] = normalizedStates.join(',')
          states.clear()
          normalizedStates.forEach((state) => states.add(state))
        }
      }

      // Normalize counties if we have exactly one state
      if (counties.size > 0 && states.size === 1) {
        const stateCode = Array.from(states)[0]
        const countiesArray = Array.from(counties)

        const { normalizedCounties } = normalizeCountiesForState(
          countiesArray,
          stateCode,
          mappingData.state_and_counties_map,
        )

        // Update the row with normalized county values and the counties Set
        if (normalizedCounties.length > 0) {
          row[FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES] = normalizedCounties.join(',')
          counties.clear()
          normalizedCounties.forEach((county) => counties.add(county))
        }
      }
    }

    rowLocationMap.set(row.rowNumber, { states: states, counties: counties, zipCodes: zipCodes })
    const rowKey = createRowKey({ ...row, states, counties, zipCodes }, availableColumnsMap)

    if (!productGroups.has(productName)) {
      productGroups.set(productName, [])
    }
    productGroups.get(productName).push({ rowNumber: row.rowNumber, rowKey, row })
  })
}

// Update validateEntities to include specific location validation
const validateEntities = (row, validationState, summary, overrideType, mappingData) => {
  // Validate product exists
  const productName = row[FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME]?.trim()
  if (productName) {
    const productExists = mappingData.products.some((p) => p.description === productName)
    if (!productExists) {
      addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME, FeeValidationErrorTypes.INVALID_VALUE, {
        value: productName,
      })
      summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
    }
  }

  if (
    overrideType === OVERRIDE_TYPES.BRANCH.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_LOCATION.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_VENDOR.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_VENDOR_LOCATION.value
  ) {
    const branchName = row[FEE_MGMT_COLS_COLUMN_KEYS.BRANCH]?.trim()
    if (branchName) {
      const branchExists = mappingData.branches.some((b) => b.name === branchName)
      if (!branchExists) {
        addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.BRANCH, FeeValidationErrorTypes.INVALID_VALUE, {
          value: branchName,
        })
        summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
      }
    }
  }

  // Validate vendor exists if needed
  if (
    overrideType === OVERRIDE_TYPES.VENDOR.value ||
    overrideType === OVERRIDE_TYPES.VENDOR_LOCATION.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_VENDOR.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_VENDOR_LOCATION.value
  ) {
    const vendorName = row[FEE_MGMT_COLS_COLUMN_KEYS.VENDOR]?.trim()
    if (vendorName) {
      const vendorExists = mappingData.panelists.some((p) => p.name === vendorName)
      if (!vendorExists) {
        addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.VENDOR, FeeValidationErrorTypes.INVALID_VALUE, {
          value: vendorName,
        })
        summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
      }
    }
  }

  // Location validations if needed (only applies for location-related override types)
  if (
    overrideType === OVERRIDE_TYPES.LOCATION.value ||
    overrideType === OVERRIDE_TYPES.VENDOR_LOCATION.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_LOCATION.value ||
    overrideType === OVERRIDE_TYPES.BRANCH_VENDOR_LOCATION.value
  ) {
    // State validation - handle both abbreviations and full state names (case-insensitive)
    const statesRaw =
      row.states
        ?.split(',')
        .map((s) => s.trim())
        .filter(Boolean) || []
    if (statesRaw.length > 0) {
      const { normalizedStates, invalidStates } = normalizeStatesInput(statesRaw, mappingData.state_and_counties_map)

      if (invalidStates.length > 0) {
        addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.STATES, FeeValidationErrorTypes.INVALID_VALUE, {
          value: invalidStates.join(', '),
        })
        summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
      }

      // Update the row with normalized state values for consistency
      if (normalizedStates.length > 0) {
        row.states = normalizedStates.join(',')
      }
    }

    // County validation - handle case-insensitive and special character normalization
    const locationType = row[FEE_MGMT_COLS_COLUMN_KEYS.LOCATION_TYPE]?.trim()
    const countiesRaw =
      row.counties
        ?.split(',')
        .map((c) => c.trim())
        .filter(Boolean) || []
    // Use normalized states from the validation above
    const normalizedStatesArr = row.states?.split(',').map((s) => s.trim()) || []
    if (locationType === LOCATION_TYPES.COUNTY && normalizedStatesArr.length === 1 && countiesRaw.length > 0) {
      const state = normalizedStatesArr[0] // Counties are associated with a single state

      // Use the new county normalization function
      const { normalizedCounties, invalidCounties } = normalizeCountiesForState(
        countiesRaw,
        state,
        mappingData.state_and_counties_map,
      )

      if (invalidCounties.length > 0) {
        addError(validationState, FEE_MGMT_COLS_COLUMN_KEYS.COUNTIES, FeeValidationErrorTypes.INVALID_VALUE, {
          value: invalidCounties,
        })
        summary.errorRows[FeeValidationErrorTypes.INVALID_VALUE].add(row.rowNumber)
      }

      // Update the row with normalized county values for consistency
      if (normalizedCounties.length > 0) {
        row.counties = normalizedCounties.join(',')
      }
    }
  }
}

export const useValidation = () => {
  const validationCache = useMemo(() => new Map(), [])

  const getRequiredColumns = useCallback((overrideType, selectedFields, readable = false) => {
    const requiredColumns = readable ? REQUIRED_COLUMNS_BY_OVERRIDE_TYPE_READABLE : REQUIRED_COLUMNS_BY_OVERRIDE_TYPE
    return {
      requiredColumns: [...(requiredColumns.common || []), ...(requiredColumns[overrideType] || [])],
      overrideColumns: selectedFields.filter((field) =>
        readable ? OVERRIDE_COLUMNS_READABLE.includes(field) : OVERRIDE_COLUMNS_KEYS.includes(field),
      ),
    }
  }, [])

  const getRequiredColumnsMapping = useCallback((columnsSet) => {
    // Iterate over all defined columns in FEE_MGMT_COLUMNS
    return Object.keys(FEE_MGMT_COLUMNS).reduce((acc, feeMgmtEnumKey) => {
      // Get the column key string (e.g., "productName") for the current enum key (e.g., "PRODUCT_NAME")
      const columnKeyString = FEE_MGMT_COLUMNS[feeMgmtEnumKey].columnKey
      // Check if this column key string is present in the set of required/selected columns
      acc[feeMgmtEnumKey] = columnsSet.has(columnKeyString)
      return acc
    }, {})
  }, [])

  const validateHeaders = useCallback(
    (headers, overrideType, selectedFields) => {
      const requiredColumns = getRequiredColumns(overrideType, selectedFields)
      const errors = []
      const extraColumns = []

      // Check for required columns
      requiredColumns.forEach((column) => {
        if (!headers.has(column)) {
          errors.push(`Missing required column: ${column}`)
        }
      })

      // Find extra columns and filter them out from headers
      const allowedColumns = new Set(requiredColumns)
      const filteredHeaders = new Set()

      headers.forEach((header) => {
        if (allowedColumns.has(header)) {
          filteredHeaders.add(header)
        } else {
          extraColumns.push(header)
        }
      })

      const result = {
        isValid: errors.length === 0,
        errors,
        requiredColumns,
        extraColumns,
        headers: filteredHeaders, // Return filtered headers
      }

      return result
    },
    [getRequiredColumns],
  )

  const validateRows = useCallback(
    (rows, { overrideType, overrideFields, mappingData }) => {
      console.log('validateRows called')

      const summary = createValidationSummary()
      const stateKeyToRows = new Map()
      const countyKeyToRows = new Map()
      const zipKeyToRows = new Map()
      const vendorKeyToRows = new Map()
      const branchKeyToRows = new Map()
      const productGroups = new Map()
      const markedDuplicateRows = new Set()
      const duplicateRowsMap = new Map()
      const columnsForValidation = getRequiredColumns(overrideType, overrideFields)
      const columnsSet = new Set([...columnsForValidation.requiredColumns, ...columnsForValidation.overrideColumns])
      const availableColumnsMap = getRequiredColumnsMapping(columnsSet)

      const rowLocationMap = new Map()
      createProductGroups(rows, availableColumnsMap, productGroups, rowLocationMap, mappingData)
      markDuplicateRows(productGroups, markedDuplicateRows, duplicateRowsMap)

      const processedRows = rows.map((row) => {
        const productName = row[FEE_MGMT_COLS_COLUMN_KEYS.PRODUCT_NAME] || ''
        const vendor = availableColumnsMap.VENDOR ? row[FEE_MGMT_COLS_COLUMN_KEYS.VENDOR] : null
        const branch = availableColumnsMap.BRANCH ? row[FEE_MGMT_COLS_COLUMN_KEYS.BRANCH] : null
        const { states, counties, zipCodes } = rowLocationMap.get(row.rowNumber)

        // Create validation state
        const validationState = {
          isDuplicateRow: markedDuplicateRows.has(row.rowNumber),
          userMarkedForDelete: row.userMarkedForDelete || false,
          validationErrors: {},
        }

        // Add separate property for duplicate information if it's a duplicate row
        if (validationState.isDuplicateRow) {
          summary.errorRows[FeeValidationErrorTypes.DUPLICATE_ROW].add(row.rowNumber)
          const originalRowNumber = duplicateRowsMap.get(row.rowNumber)

          // Add a dedicated property for duplicate information
          validationState.duplicateOf = {
            rowNumber: originalRowNumber,
            message: `Duplicate of row ${originalRowNumber}`,
          }

          return { validationState, row, states, counties, zipCodes, productName }
        }

        validateMissingValues({ ...row, states, counties, zipCodes }, validationState, summary, availableColumnsMap)

        // Add the new location validation
        validateLocations({ ...row, states, counties, zipCodes }, validationState, summary, columnsSet)

        // Now only validates Fee and Due Date numeric formats
        validateFormats({ ...row, states, counties, zipCodes }, validationState, summary, columnsSet)

        trackLocations(
          { validationState, row, states, counties, zipCodes, vendor, productName, branch, availableColumnsMap },
          { stateKeyToRows, countyKeyToRows, zipKeyToRows },
        )

        trackVendors(validationState, vendor, productName, vendorKeyToRows, availableColumnsMap)

        trackBranches(validationState, branch, productName, branchKeyToRows, availableColumnsMap)

        // Add entity validation
        if (mappingData) {
          validateEntities(row, validationState, summary, overrideType, mappingData)
        }

        return { validationState, row, states, counties, zipCodes, productName }
      })

      checkDuplicateLocations(stateKeyToRows, countyKeyToRows, zipKeyToRows, summary)

      const finalRows = processedRows.map(({ validationState, row, states, counties, zipCodes }) => {
        const mappedRow = {
          rowNumber: row.rowNumber,
        }

        // Excludes any extra columns from a file upload
        Object.values(FEE_MGMT_COLUMNS).forEach((column) => {
          if (column.columnKey in row) {
            mappedRow[column.columnKey] = row[column.columnKey]
          }
        })

        const result = {
          ...mappedRow,
          // Preserve the server override id (not a FEE_MGMT column) so edits/deletes
          // of an existing row target it (UpdateOne) instead of inserting a duplicate.
          ...(row._id ? { _id: row._id } : {}),
          isDuplicateRow: validationState.isDuplicateRow,
          userMarkedForDelete: validationState.userMarkedForDelete,
          validationErrors: validationState.validationErrors,
        }

        // Add the duplicateOf property if it exists
        if (validationState.duplicateOf) {
          result.duplicateOf = validationState.duplicateOf
        }

        const rowKey = createRowKey({ ...row, states, counties, zipCodes }, availableColumnsMap)
        const cacheKey = createCacheKey(row, rowKey)
        validationCache.set(cacheKey, result)

        return result
      })

      Object.keys(summary.errorRows).forEach((errorType) => {
        summary.errorCounts[errorType] = summary.errorRows[errorType].size
      })

      summary.totalErrorCount = Object.values(summary.errorCounts).reduce((a, b) => a + b, 0)
      summary.hasErrors = summary.totalErrorCount > 0

      return { rows: finalRows, summary }
    },
    [validationCache, getRequiredColumns, getRequiredColumnsMapping],
  )

  return {
    validateRows,
    validateHeaders,
    getRequiredColumns,
  }
}
